import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Check,
  Download,
  FileText,
  Loader2,
  Send,
  Sparkles,
  Truck,
  Shield,
  ShieldCheck,
  Clock,
  MapPin,
  XCircle,
} from "lucide-react";
import { api, API, formatApiError } from "../lib/api";
import { formatEUR, countryFlag } from "../lib/format";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const DEALER_STEPS = [
  "richiesta_ricevuta",
  "verifica_in_corso",
  "trattativa",
  "accordo_raggiunto",
  "documenti",
  "pagamento",
  "trasporto_consegna",
  "conclusa",
];

export default function PraticaDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [pratica, setPratica] = useState(null);
  const [tab, setTab] = useState("timeline");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/pratiche/${id}`).then((r) => setPratica(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!pratica) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const v = pratica.vehicle;
  const cancelled = pratica.cancelled;
  const currentIdx = DEALER_STEPS.indexOf(pratica.dealer_status);
  const isSeller = pratica.viewer_role === "seller";
  const displayCommission = isSeller
    ? (pratica.commission?.seller_share_eur || 0)
    : (pratica.commission?.buyer_share_eur || 0);
  // Buyer-view legacy alias kept for other places (removed once no stale references remain).
  const buyerCommission = displayCommission;

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/pratiche/${pratica.id}/messages`, { text: msg });
      setMsg("");
      load();
      toast.success(t("pratiche.message_sent"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="py-6 md:py-10" data-testid={`pratica-detail-${pratica.id}`}>
      <Link
        to="/pratiche"
        data-testid="pratica-back"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("pratiche.back")}
      </Link>

      {/* Hero */}
      <div className="rounded-2xl bg-[#0A0A0B] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400 font-semibold inline-flex items-center gap-2" data-testid="pratica-code">
              {pratica.code} · {t(`pratiche.type.${pratica.type}`)}
              {isSeller && (
                <span
                  data-testid="pratica-viewer-badge"
                  className="rounded-full bg-blue-500/25 text-blue-100 px-2 py-0.5 text-[9px] tracking-[0.18em] font-semibold"
                >
                  {t("pratiche.viewer_role_seller")}
                </span>
              )}
              {!isSeller && (
                <span
                  data-testid="pratica-viewer-badge"
                  className="rounded-full bg-slate-800/60 text-slate-300 px-2 py-0.5 text-[9px] tracking-[0.18em] font-semibold"
                >
                  {t("pratiche.viewer_role_buyer")}
                </span>
              )}
            </div>
            <h1 className="mt-1 font-heading font-bold text-3xl md:text-4xl tracking-tighter">
              {v ? `${v.brand} ${v.model}` : t("pratiche.title")}
            </h1>
            {v && (
              <p className="text-slate-300 text-sm mt-1">
                {v.trim} · {v.year} · {countryFlag(v.country)} {v.country} →{" "}
                {countryFlag(pratica.country_to)} {pratica.country_to}
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              {pratica.negotiated_price ? t("pratiche.negotiated") : t("pratiche.price_requested")}
            </div>
            <div className="mt-1 font-heading font-bold text-2xl tabular-nums">
              {formatEUR(pratica.negotiated_price || v?.price_eur || pratica.requested_price || 0)}
            </div>
          </div>
        </div>
      </div>

      {cancelled && (
        <div
          data-testid="pratica-cancelled-banner"
          className="mt-4 rounded-xl bg-red-50 border border-red-200 text-red-900 px-4 py-3 text-sm inline-flex items-center gap-2"
        >
          <XCircle className="w-4 h-4" />
          {t("pratiche.cancelled_banner")}
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-2 border-b border-slate-200 overflow-x-auto">
        {["timeline", "documents", "chat"].map((tk) => (
          <button
            key={tk}
            data-testid={`pratica-tab-${tk}`}
            onClick={() => setTab(tk)}
            className={`px-4 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === tk
                ? "text-blue-600 border-blue-600"
                : "text-slate-500 border-transparent hover:text-slate-900"
            }`}
          >
            {t(`pratiche.tab.${tk}`)}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {isSeller && (
            <SellerActionsPanel pratica={pratica} onReload={load} />
          )}
          {tab === "timeline" && <TimelinePanel pratica={pratica} currentIdx={currentIdx} />}
          {tab === "documents" && <DocumentsPanel pratica={pratica} />}
          {tab === "chat" && (
            <ChatPanel pratica={pratica} msg={msg} setMsg={setMsg} sendMsg={sendMsg} sending={sending} />
          )}

          {/* Activity relevant to the dealer */}
          {pratica.activity_history?.length > 0 && (
            <ActivityPanel activity={pratica.activity_history} />
          )}

          {/* AUTEXIO service strip */}
          <ServiceStrip />
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {v && (
            <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden shadow-sm">
              {v.images?.[0] && (
                <img src={v.images[0]} alt="" className="w-full aspect-[16/10] object-cover" />
              )}
              <div className="p-4">
                <div className="font-heading font-semibold text-sm">
                  {v.brand} {v.model}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{v.trim}</p>
                <Link
                  to={`/vehicle/${v.id}`}
                  className="mt-3 inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  {t("pratiche.view_vehicle")}
                </Link>
              </div>
            </div>
          )}

          {/* Commission (transparent, side-scoped share) */}
          <div
            data-testid="pratica-commission"
            className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold inline-flex items-center gap-1">
              <Shield className="w-3 h-3" />{" "}
              {isSeller
                ? t("pratiche.commission_title_seller")
                : t("pratiche.commission_title")}
            </div>
            <div className="mt-2 font-heading font-bold text-2xl tabular-nums text-slate-950">
              {formatEUR(displayCommission)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {pratica.commission?.tier_label}
            </div>
            <Link
              to="/tariffario"
              data-testid="pratica-commission-link"
              className="mt-3 inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-700"
            >
              {t("pratiche.commission_view_all")}
            </Link>
          </div>

          {/* Transport summary */}
          {pratica.transport_required && (
            <div className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold inline-flex items-center gap-1">
                <Truck className="w-3 h-3" /> {t("pratiche.transport_title")}
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-semibold">
                  {pratica.delivery_city || "—"}
                </span>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {t("pratiche.transport_status")}
              </div>
              {pratica.transport_cost != null && (
                <div className="mt-2 text-sm">
                  <span className="text-slate-500">{t("pratiche.transport_cost")}:</span>{" "}
                  <span className="font-heading font-bold tabular-nums text-slate-950">
                    {formatEUR(pratica.transport_cost)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Protected payment info (dealer-facing prudent copy) */}
          <ProtectedPaymentCard dealerStatus={pratica.dealer_status} />

          {/* Responsabile */}
          <div className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              {t("pratiche.responsabile")}
            </div>
            <div className="mt-1 font-heading font-semibold text-sm">
              {pratica.responsabile || t("pratiche.responsabile_default")}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t("pratiche.updated_at")}:{" "}
              {new Date(pratica.updated_at || pratica.created_at).toLocaleDateString("it-IT", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function TimelinePanel({ pratica, currentIdx }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="pratica-timeline"
      className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm"
    >
      <h3 className="font-heading font-semibold text-lg tracking-tight text-slate-950 mb-6">
        {t("pratiche.timeline_title")}
      </h3>
      <ol className="relative">
        {pratica.timeline?.map((step, i) => {
          const isLast = i === pratica.timeline.length - 1;
          const done = step.status === "completed";
          const cur = step.status === "current";
          const cancelled = step.status === "cancelled";
          return (
            <li key={step.step} className="pl-10 pb-7 relative">
              {!isLast && (
                <span
                  className={`absolute left-[15px] top-8 bottom-0 w-px ${
                    done ? "bg-emerald-400" : "bg-slate-200"
                  }`}
                />
              )}
              <span
                className={`absolute left-0 top-1 w-8 h-8 rounded-full flex items-center justify-center border-2 font-bold text-xs ${
                  cancelled
                    ? "bg-slate-100 border-slate-300 text-slate-400"
                    : done
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : cur
                    ? "bg-white border-blue-600 text-blue-600 ring-4 ring-blue-100"
                    : "bg-white border-slate-200 text-slate-400"
                }`}
              >
                {done ? <Check className="w-4 h-4" /> : i + 1}
              </span>
              <div
                className={`font-heading font-semibold text-sm ${
                  cur ? "text-slate-950" : done ? "text-slate-900" : "text-slate-400"
                }`}
                data-testid={`pratica-timeline-step-${step.step}`}
              >
                {t(`pratiche.step.${step.step}`)}
              </div>
              {step.date && !cancelled && (
                <div className="text-xs text-slate-500 mt-0.5 tabular-nums">
                  {new Date(step.date).toLocaleDateString("it-IT", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function DocumentsPanel({ pratica }) {
  const { t } = useTranslation();
  const token = localStorage.getItem("autexio_token");
  const available = pratica.documents_available || [];
  const missing = pratica.documents_missing || [];

  const download = async (slug, name) => {
    try {
      const resp = await fetch(`${API}/pratiche/${pratica.id}/documents/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(t("common.error_generic"));
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
      <h3 className="font-heading font-semibold text-lg tracking-tight text-slate-950 mb-4">
        {t("pratiche.documents_title")}
      </h3>

      {available.length === 0 && missing.length === 0 && (
        <p className="text-sm text-slate-500">{t("pratiche.documents_empty")}</p>
      )}

      {available.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-2 mb-2">
            {t("pratiche.documents_available")}
          </div>
          <ul className="space-y-2" data-testid="pratica-documents-available">
            {available.map((d) => (
              <li key={d.slug}>
                <button
                  data-testid={`pratica-doc-download-${d.slug}`}
                  onClick={() => download(d.slug, d.name)}
                  className="w-full flex items-center gap-3 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/40 p-3 text-left transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-sm truncate">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.size_kb} KB · PDF</div>
                  </div>
                  <Download className="w-4 h-4 text-slate-400" />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {missing.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-6 mb-2">
            {t("pratiche.documents_missing")}
          </div>
          <ul className="space-y-1" data-testid="pratica-documents-missing">
            {missing.map((d) => (
              <li
                key={d.slug}
                className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 p-3 text-slate-500 text-sm"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.name}</div>
                  <div className="text-xs text-slate-400">{t("pratiche.doc_not_yet")}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ChatPanel({ pratica, msg, setMsg, sendMsg, sending }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col min-h-[420px]">
      <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="pratica-chat">
        {(pratica.messages || []).map((m, i) => (
          <div
            key={i}
            className={`flex ${m.author === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.author === "user"
                  ? "bg-blue-600 text-white rounded-br-sm"
                  : "bg-slate-50 border border-slate-100 text-slate-800 rounded-bl-sm"
              }`}
              data-testid={`pratica-chat-${m.author}-${i}`}
            >
              <div>{m.text}</div>
              <div
                className={`text-[10px] mt-1 tabular-nums ${
                  m.author === "user" ? "text-blue-100" : "text-slate-400"
                }`}
              >
                {new Date(m.ts).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
              </div>
            </div>
          </div>
        ))}
      </div>
      <form
        onSubmit={sendMsg}
        className="border-t border-slate-100 p-3 flex items-center gap-2"
      >
        <input
          type="text"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder={t("pratiche.chat_placeholder")}
          data-testid="pratica-chat-input"
          className="flex-1 h-10 rounded-full border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-500"
        />
        <Button
          type="submit"
          disabled={sending || !msg.trim()}
          data-testid="pratica-chat-send"
          className="h-10 w-10 rounded-full p-0 bg-blue-600 hover:bg-blue-700"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}

function ActivityPanel({ activity }) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
      <h3 className="font-heading font-semibold text-lg tracking-tight text-slate-950 mb-4">
        {t("pratiche.activity_title")}
      </h3>
      <ul className="space-y-3">
        {activity.slice(-8).map((ev, i) => (
          <li key={i} className="flex items-start gap-3 text-sm">
            <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />
            <div className="flex-1">
              <div className="text-slate-800">
                {t(`pratiche.dealer_activity.${ev.key}`)}
              </div>
              {ev.date && (
                <div className="text-xs text-slate-400 tabular-nums mt-0.5">
                  {new Date(ev.date).toLocaleDateString("it-IT", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ServiceStrip() {
  const { t } = useTranslation();
  const items = ["trattativa", "verifica", "documenti", "pagamenti", "trasporto", "consegna"];
  return (
    <div className="mt-6 rounded-2xl bg-gradient-to-br from-slate-950 to-slate-900 text-white p-6 relative overflow-hidden">
      <div className="absolute -right-16 -bottom-16 w-48 h-48 rounded-full bg-blue-600/20 blur-3xl" />
      <div className="relative z-10">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-300">
          <Sparkles className="w-3 h-3" /> {t("pratiche.service_strip_overline")}
        </div>
        <h3 className="mt-2 font-heading font-bold text-lg tracking-tight max-w-lg">
          {t("pratiche.service_strip_title")}
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {items.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-100 bg-white/10 border border-white/20 rounded-full px-2.5 py-1"
            >
              {t(`pratiche.service_item.${k}`)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

const PAYMENT_STAGES = new Set([
  "accordo_raggiunto",
  "documenti",
  "pagamento",
  "trasporto_consegna",
  "conclusa",
]);

function ProtectedPaymentCard({ dealerStatus }) {
  const { t } = useTranslation();
  const activated = PAYMENT_STAGES.has(dealerStatus);
  const bullets = t("pratiche.protected_payment.future_flow", { returnObjects: true });
  const items = Array.isArray(bullets) ? bullets : [];
  return (
    <div
      data-testid="pratica-protected-payment"
      className="rounded-2xl bg-white border border-slate-100 p-4 shadow-sm"
    >
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold inline-flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" /> {t("pratiche.protected_payment.title")}
      </div>
      <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5">
        <Clock className="w-2.5 h-2.5" />
        {t(
          activated
            ? "pratiche.protected_payment.badge_manual"
            : "pratiche.protected_payment.badge_incoming"
        )}
      </div>
      <p className="mt-2 text-xs text-slate-600 leading-relaxed">
        {t("pratiche.protected_payment.summary")}
      </p>
      {items.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {items.map((label, i) => (
            <li
              key={i}
              className="text-[11px] text-slate-500 leading-snug flex items-start gap-1.5"
            >
              <span className="mt-1 w-1 h-1 rounded-full bg-slate-300 shrink-0" />
              <span>{label}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-[10px] italic text-slate-400 leading-snug border-t border-slate-100 pt-2">
        {t("pratiche.protected_payment.disclaimer")}
      </p>
    </div>
  );
}


function SellerActionsPanel({ pratica, onReload }) {
  const { t } = useTranslation();
  const state = pratica.seller_state || "pending";
  const [mode, setMode] = React.useState(null); // null | 'accept' | 'decline' | 'counter'
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");
  const [counterPrice, setCounterPrice] = React.useState(
    pratica.negotiated_price || pratica.target_price || pratica.vehicle?.price_eur || ""
  );
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "accept") {
        await api.post(`/pratiche/${pratica.id}/seller/accept`, { note: note || null });
      } else if (mode === "decline") {
        if (!reason.trim()) {
          toast.error(t("pratiche.seller_actions.decline_reason"));
          setBusy(false);
          return;
        }
        await api.post(`/pratiche/${pratica.id}/seller/decline`, { reason });
      } else if (mode === "counter") {
        const price = parseFloat(counterPrice);
        if (!(price > 0)) {
          toast.error(t("pratiche.seller_actions.counter_price"));
          setBusy(false);
          return;
        }
        await api.post(`/pratiche/${pratica.id}/seller/counter-price`, {
          counter_price: price,
          note: note || null,
        });
      }
      toast.success(t("pratiche.seller_actions.sent"));
      setMode(null);
      setReason("");
      setNote("");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const statePill = {
    pending:   { key: "state_pending",   cls: "bg-slate-100 text-slate-600" },
    accepted:  { key: "state_accepted",  cls: "bg-emerald-100 text-emerald-700" },
    declined:  { key: "state_declined",  cls: "bg-red-100 text-red-700" },
    countered: { key: "state_countered", cls: "bg-amber-100 text-amber-700" },
  }[state] || { key: "state_pending", cls: "bg-slate-100 text-slate-600" };

  return (
    <div
      data-testid="pratica-seller-actions"
      className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm mb-6"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
            {t("pratiche.seller_actions.title")}
          </div>
          <div className="mt-1 inline-flex items-center gap-2">
            <span
              data-testid="pratica-seller-state"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statePill.cls}`}
            >
              {t(`pratiche.seller_actions.${statePill.key}`)}
            </span>
            {state === "declined" && pratica.seller_decline_reason && (
              <span className="text-xs text-slate-500">
                {t("pratiche.seller_actions.declined_reason_prefix")} {pratica.seller_decline_reason}
              </span>
            )}
            {state === "countered" && pratica.seller_counter_price != null && (
              <span className="text-xs text-slate-500">
                {t("pratiche.seller_actions.counter_prefix")}{" "}
                <span className="font-heading font-bold tabular-nums text-slate-900">
                  {formatEUR(pratica.seller_counter_price)}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {mode === null && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="pratica-seller-accept"
            onClick={() => setMode("accept")}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 transition-colors"
          >
            <Check className="w-3.5 h-3.5" /> {t("pratiche.seller_actions.accept")}
          </button>
          <button
            type="button"
            data-testid="pratica-seller-counter"
            onClick={() => setMode("counter")}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-950 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 transition-colors"
          >
            {t("pratiche.seller_actions.counter")}
          </button>
          <button
            type="button"
            data-testid="pratica-seller-decline"
            onClick={() => setMode("decline")}
            className="inline-flex items-center gap-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold px-4 py-2 transition-colors"
          >
            <XCircle className="w-3.5 h-3.5" /> {t("pratiche.seller_actions.decline")}
          </button>
        </div>
      )}

      {mode === "accept" && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">{t("pratiche.seller_actions.accept_desc")}</p>
          <textarea
            data-testid="pratica-seller-accept-note"
            placeholder={t("pratiche.seller_actions.note_optional")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-slate-200 bg-white p-2 text-sm"
          />
          <SellerActionButtons busy={busy} onCancel={() => setMode(null)} onSubmit={submit} />
        </div>
      )}

      {mode === "decline" && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <label className="text-xs font-semibold text-slate-600">
            {t("pratiche.seller_actions.decline_reason")}
          </label>
          <textarea
            data-testid="pratica-seller-decline-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            className="w-full rounded-md border border-slate-200 bg-white p-2 text-sm"
          />
          <SellerActionButtons busy={busy} onCancel={() => setMode(null)} onSubmit={submit} />
        </div>
      )}

      {mode === "counter" && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <label className="text-xs font-semibold text-slate-600">
            {t("pratiche.seller_actions.counter_price")}
          </label>
          <input
            data-testid="pratica-seller-counter-price"
            type="number"
            value={counterPrice}
            onChange={(e) => setCounterPrice(e.target.value)}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums"
          />
          <textarea
            data-testid="pratica-seller-counter-note"
            placeholder={t("pratiche.seller_actions.note_optional")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-slate-200 bg-white p-2 text-sm"
          />
          <SellerActionButtons busy={busy} onCancel={() => setMode(null)} onSubmit={submit} />
        </div>
      )}

      <p className="mt-3 text-[10px] italic text-slate-400 leading-snug border-t border-slate-100 pt-2">
        {t("pratiche.seller_actions.hint")}
      </p>
    </div>
  );
}

function SellerActionButtons({ busy, onCancel, onSubmit }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-full px-4 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
      >
        {t("common.cancel")}
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={busy}
        data-testid="pratica-seller-submit"
        className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-1.5 disabled:opacity-60"
      >
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {t("pratiche.seller_actions.submit")}
      </button>
    </div>
  );
}

